import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Material UI Icons
import CategoryIcon from '@mui/icons-material/Category';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

import { useCategory } from '../context/CategoryContext';
import { Category } from '../models/Category';

export default function CategoryCard({
  category,
  getCategoriesData,
  showParent = false,
  inParameters = false,
  onCategoryClick,
}: {
  category: Category;
  getCategoriesData?: (categoryId?: number) => Promise<void>;
  showParent?: boolean;
  inParameters?: boolean;
  onCategoryClick?: (categoryId: number) => void;
}) {
  const { deleteCategory } = useCategory();

  const handleDeleteCategory = async () => {
    const toastId = toast.loading('Usuwanie...', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: 0,
      theme: 'light',
    });

    try {
      await deleteCategory(category.id);
      toast.update(toastId, {
        render: 'Kategoria została usunięta!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      // Odświeżenie listy kategorii po usunięciu
      if (getCategoriesData) {
        if (category.parentCategoryId) {
          await getCategoriesData(category.parentCategoryId);
        } else {
          await getCategoriesData();
        }
      }
    } catch (error: any) {
      console.error('Błąd podczas usuwania kategorii', error);
      toast.update(toastId, {
        render: error.message || 'Nie udało się usunąć kategorii',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleDeleteClick = () => {
    confirmAlert({
      title: 'Potwierdź usunięcie',
      message: `Czy na pewno chcesz usunąć kategorię "${category.name}"? Ta operacja jest nieodwracalna i usunie również wszystkie podkategorie.`,
      buttons: [
        {
          label: 'Usuń',
          onClick: handleDeleteCategory,
          className: 'bg-red-500 hover:bg-red-600 text-white',
        },
        {
          label: 'Anuluj',
          className: 'bg-gray-200 hover:bg-gray-300 text-gray-800',
        },
      ],
      closeOnEscape: true,
      closeOnClickOutside: true,
      overlayClassName: 'react-confirm-alert-overlay-custom',
    });
  };

  const handleCategoryClick = (e: React.MouseEvent) => {
    if (onCategoryClick) {
      e.preventDefault();
      onCategoryClick(category.id);
    }
  };

  const handleParentCategoryClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (getCategoriesData) {
      if (category.parentCategoryId) {
        getCategoriesData(category.parentCategoryId);
      } else {
        getCategoriesData();
      }
    }
  };

  // Liczba podkategorii
  const subcategoriesCount = category.subCategories?.length || 0;

  return (
    <motion.div
      className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
      whileHover={{
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      }}
      transition={{ duration: 0.2 }}
    >
      {/* Kategoria nadrzędna (jeśli pokazywana) */}
      {showParent && (
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
          <button
            onClick={handleParentCategoryClick}
            className="flex items-center gap-2 text-sm text-primary hover:text-primary-dark transition-colors"
          >
            <ArrowUpwardIcon fontSize="small" />
            <span>
              {category.parentCategory
                ? `Kategoria nadrzędna: ${category.parentCategory.name}`
                : 'Kategoria główna'}
            </span>
          </button>
        </div>
      )}

      {/* Główna zawartość karty */}
      <div className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Informacje o kategorii */}
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-2 rounded-lg">
              {category.image ? (
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-6 h-6 object-contain"
                />
              ) : (
                <CategoryIcon className="text-primary" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">
                {category.name}
              </h3>
            </div>
          </div>

          {/* Przyciski akcji */}
          {!inParameters && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleCategoryClick}
                className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-1.5 px-3 rounded transition-colors text-sm font-medium"
              >
                <KeyboardArrowRightIcon fontSize="small" />
                <span>Pokaż podkategorie</span>
                {subcategoriesCount > 0 && (
                  <span className="ml-1 bg-gray-200 text-gray-700 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                    {subcategoriesCount}
                  </span>
                )}
              </button>
              <Link
                to={`/admin/kategorie/${category.id}/dodaj`}
                className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white py-1.5 px-3 rounded transition-colors text-sm font-medium"
              >
                <AddIcon fontSize="small" />
                <span>Dodaj</span>
              </Link>
              <Link
                to={`/admin/kategorie/${category.id}/edytuj`}
                className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white py-1.5 px-3 rounded transition-colors text-sm font-medium"
              >
                <EditIcon fontSize="small" />
                <span>Edytuj</span>
              </Link>
              <button
                onClick={handleDeleteClick}
                className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white py-1.5 px-3 rounded transition-colors text-sm font-medium"
              >
                <DeleteIcon fontSize="small" />
                <span>Usuń</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dodatkowe informacje */}
      {subcategoriesCount > 0 && (
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-100 flex justify-between items-center text-sm text-gray-500">
          <span>
            Podkategorie:{' '}
            <span className="font-semibold text-gray-700">
              {subcategoriesCount}
            </span>
          </span>
        </div>
      )}
    </motion.div>
  );
}
